import { useContext } from 'react';
import { T, useGT, Var } from 'gt-react';
import { ArrowsLeftRightIcon, TrashIcon } from '@phosphor-icons/react';
import {
  TASK_PROCEDURE_PARAM_ANCHOR_ID,
  TASK_PROCEDURE_PARAM_ANCHOR_TYPE,
} from '@erp/shared-types';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Badge } from 'src/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { AuthContext } from 'src/context/auth.context';
import {
  useSop,
  useSopVersions,
} from 'src/api/rpc/hooks/agent/useOperatorSops';
import {
  newProcedureParamDraft,
  type TaskTypeProcedureDraft,
  type TaskTypeProcedureParamDraft,
} from './taskTypeAdmin';
import {
  BINDING_CUSTOM,
  BINDING_LITERAL,
  BINDING_UNMAPPED,
  applyNameMatches,
  bindingSelectValue,
  valueAfterBindingChoice,
  inputFieldBinding,
  inputFieldOptionLabel,
  matchUnmappedParamsByFieldName,
  missingSchemaNotice,
  parameterTextMode,
  pinnedProcedureVersion,
  procedureMapperPhase,
  procedureParameterView,
  procedureParamsAreLoading,
  sopQueryId,
  waitingForPinnedVersion,
  setProcedureParamValue,
  showInputFieldGroup,
  unknownInputField,
  unmappedRequiredParams,
  rowNeedsTaskValue,
  rowsHaveInputBinding,
  type MappedProcedureParam,
  type ProcedureInputField,
  type ProcedureInputSchemaStatus,
  type ProcedureParamRow,
} from './procedureParameterBinding';

type SchemaStatus = ProcedureInputSchemaStatus;

/**
 * Map each parameter the procedure declares onto a task input field, the
 * anchor, or a fixed value. Stored as the existing `$.task.*` binding language.
 */
export function ProcedureParameterMapper({
  draft,
  disabled,
  fields,
  schemaStatus,
  onUpdate,
}: {
  draft: TaskTypeProcedureDraft;
  disabled?: boolean;
  fields: readonly ProcedureInputField[];
  schemaStatus: SchemaStatus;
  onUpdate: (patch: Partial<TaskTypeProcedureDraft>) => void;
}) {
  const model = useProcedureParameterModel(draft);
  const phase = procedureMapperPhase({
    sopId: draft.sopId,
    waiting: model.waiting,
    empty: model.empty,
  });
  if (phase === 'choose') return <ChooseProcedure />;
  if (phase === 'loading') return <LoadingParameters />;
  return (
    <MapperBody
      draft={draft}
      disabled={disabled}
      fields={fields}
      schemaStatus={schemaStatus}
      model={model}
      phase={phase}
      onUpdate={onUpdate}
    />
  );
}

function useProcedureParameterModel(draft: TaskTypeProcedureDraft) {
  const { currentUser } = useContext(AuthContext);
  const organizationId = currentUser?.organizationId;
  const versions = useSopVersions(organizationId, draft.sopId || undefined);
  const waitingForPin = waitingForPinnedVersion(
    draft.pinnedVersionId,
    versions.isLoading,
  );
  const pinned = pinnedProcedureVersion(
    versions.data?.versions,
    draft.pinnedVersionId,
  );
  const sop = useSop(
    organizationId,
    sopQueryId({ sopId: draft.sopId, waitingForPin }),
    pinned?.version,
  );
  return procedureParameterView({
    waiting: procedureParamsAreLoading({
      sopId: draft.sopId,
      waitingForPin,
      sopLoading: sop.isLoading,
    }),
    loadFailed: sop.isError,
    mapped: draft.params.map((row) => ({
      param: row.param,
      value: row.value,
    })),
    parameters: sop.data?.parameters,
  });
}

function ChooseProcedure() {
  return (
    <p className="text-[11px] text-muted-foreground">
      <T context="task type procedure parameters, no procedure chosen">
        Choose a procedure to map its parameters.
      </T>
    </p>
  );
}

function LoadingParameters() {
  return (
    <p className="text-[11px] text-muted-foreground">
      <T context="task type procedure parameters loading">
        Loading procedure parameters…
      </T>
    </p>
  );
}

function MapperBody({
  draft,
  disabled,
  fields,
  schemaStatus,
  model,
  phase,
  onUpdate,
}: {
  draft: TaskTypeProcedureDraft;
  disabled?: boolean;
  fields: readonly ProcedureInputField[];
  schemaStatus: SchemaStatus;
  model: ReturnType<typeof useProcedureParameterModel>;
  phase: 'empty' | 'list';
  onUpdate: (patch: Partial<TaskTypeProcedureDraft>) => void;
}) {
  const nameMatches = matchUnmappedParamsByFieldName(model.rows, fields);
  function writeMapped(next: MappedProcedureParam[]) {
    onUpdate({ params: mergeDraftParams(draft.params, next) });
  }
  return (
    <div className="flex flex-col gap-2 rounded-md bg-muted/40 p-2">
      <SchemaNotice
        schemaStatus={schemaStatus}
        hasInputBinding={rowsHaveInputBinding(model.rows)}
        hasDeclared={model.declared.length > 0}
      />
      <UnmappedRequiredNotice
        names={unmappedRequiredParams(model.rows, schemaStatus)}
      />
      {model.loadFailed ? <LoadFailed /> : null}
      {phase === 'empty' ? (
        <EmptyParameters />
      ) : (
        <ParameterList
          rows={model.rows}
          fields={fields}
          disabled={disabled}
          loadFailed={model.loadFailed}
          canMatch={nameMatches.length > 0}
          onMatch={() =>
            writeMapped(applyNameMatches(model.mapped, nameMatches))
          }
          onChange={(name, value) =>
            writeMapped(setProcedureParamValue(model.mapped, name, value))
          }
        />
      )}
    </div>
  );
}

function mergeDraftParams(
  existing: readonly TaskTypeProcedureParamDraft[],
  next: readonly MappedProcedureParam[],
): TaskTypeProcedureParamDraft[] {
  const byName = new Map(existing.map((row) => [row.param, row]));
  return next.map((row) => {
    const current = byName.get(row.param);
    if (current) return { ...current, param: row.param, value: row.value };
    return newProcedureParamDraft({ param: row.param, value: row.value });
  });
}

function LoadFailed() {
  return (
    <p className="text-[11px] text-destructive">
      <T context="task type procedure parameters failed to load">
        Procedure parameters could not be loaded. Saved mappings are still
        shown.
      </T>
    </p>
  );
}

function EmptyParameters() {
  return (
    <p className="text-[11px] text-muted-foreground">
      <T context="procedure declares no parameters">
        This procedure declares no parameters.
      </T>
    </p>
  );
}

function SchemaNotice({
  schemaStatus,
  hasInputBinding,
  hasDeclared,
}: {
  schemaStatus: SchemaStatus;
  hasInputBinding: boolean;
  hasDeclared: boolean;
}) {
  if (schemaStatus === 'loading') return <LoadingFields />;
  if (schemaStatus === 'error') return <FieldsFailed />;
  if (
    !missingSchemaNotice({ status: schemaStatus, hasDeclared, hasInputBinding })
  ) {
    return null;
  }
  return <MissingSchema />;
}

function UnmappedRequiredNotice({ names }: { names: readonly string[] }) {
  if (names.length === 0) return null;
  return (
    <p className="text-[11px] text-destructive">
      <T context="task type procedure mapping, required parameters with no task value">
        Required parameters not mapped: <Var>{names.join(', ')}</Var>. Tasks of
        this type cannot pass them in, so the agent will stop and ask for them.
        Map each one to an input field.
      </T>
    </p>
  );
}

function LoadingFields() {
  return (
    <p className="text-[11px] text-muted-foreground">
      <T context="task type procedure mapping, input schema loading">
        Loading input fields…
      </T>
    </p>
  );
}

function FieldsFailed() {
  return (
    <p className="text-[11px] text-destructive">
      <T context="task type procedure mapping, input schema failed">
        Input fields could not be loaded.
      </T>
    </p>
  );
}

function MissingSchema() {
  return (
    <p className="text-[11px] text-destructive">
      <T context="task type procedure mapping without an input schema">
        This type has no input schema, so a task has no input to pass in. Choose
        one under Behavior, then map each parameter to a field.
      </T>
    </p>
  );
}

function ParameterList({
  rows,
  fields,
  disabled,
  loadFailed,
  canMatch,
  onMatch,
  onChange,
}: {
  rows: readonly ProcedureParamRow[];
  fields: readonly ProcedureInputField[];
  disabled?: boolean;
  loadFailed: boolean;
  canMatch: boolean;
  onMatch: () => void;
  onChange: (name: string, value: string | null) => void;
}) {
  const gt = useGT();
  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] text-muted-foreground">
          <T context="help text, task type procedure parameter mapping">
            Mapped parameter values are passed into the procedure when a task
            runs.
          </T>
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 shrink-0 text-xs"
          disabled={disabled || !canMatch}
          onClick={onMatch}
        >
          <ArrowsLeftRightIcon className="size-3.5" />
          <T context="button, map procedure parameters onto same-named input fields">
            Match By Name
          </T>
        </Button>
      </div>
      <ParameterColumnHeaders />
      {rows.map((row) => (
        <ParameterRow
          key={row.name}
          row={row}
          fields={fields}
          declared={row.declared || loadFailed}
          disabled={disabled}
          onChange={(value) => onChange(row.name, value)}
          gt={gt}
        />
      ))}
    </>
  );
}

function ParameterColumnHeaders() {
  return (
    <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
      <p className="text-[11px] font-medium">
        <T context="column header, SOP parameter being mapped">
          Procedure parameter
        </T>
      </p>
      <p className="hidden text-[11px] font-medium sm:block">
        <T context="column header, task value a procedure parameter reads">
          Task value
        </T>
      </p>
    </div>
  );
}

function ParameterRow({
  row,
  fields,
  declared,
  disabled,
  onChange,
  gt,
}: {
  row: ProcedureParamRow;
  fields: readonly ProcedureInputField[];
  declared: boolean;
  disabled?: boolean;
  onChange: (value: string | null) => void;
  gt: ReturnType<typeof useGT>;
}) {
  const unknownField = unknownInputField(
    row.value,
    new Set(fields.map((field) => field.fieldName)),
  );
  return (
    <div className="grid grid-cols-1 items-start gap-1.5 sm:grid-cols-2">
      <ParameterIdentity
        row={row}
        declared={declared}
        disabled={disabled}
        onRemove={() => onChange(null)}
        gt={gt}
      />
      <ParameterValue
        value={row.value}
        fields={fields}
        unknownField={unknownField}
        disabled={disabled}
        onChange={onChange}
        gt={gt}
      />
    </div>
  );
}

function ParameterIdentity({
  row,
  declared,
  disabled,
  onRemove,
  gt,
}: {
  row: ProcedureParamRow;
  declared: boolean;
  disabled?: boolean;
  onRemove: () => void;
  gt: ReturnType<typeof useGT>;
}) {
  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="font-mono text-xs">{row.name}</span>
        {row.required ? <RequiredBadge /> : null}
        {declared ? null : (
          <RemoveBinding disabled={disabled} onRemove={onRemove} gt={gt} />
        )}
      </div>
      {row.description ? <ParameterDescription text={row.description} /> : null}
      {declared ? null : <UndeclaredNote />}
      {rowNeedsTaskValue(row) ? <UnmappedWarning /> : null}
    </div>
  );
}

function RequiredBadge() {
  return (
    <Badge variant="outline" className="text-[10px] font-normal">
      <T context="badge, procedure parameter must be mapped">Required</T>
    </Badge>
  );
}

function RemoveBinding({
  disabled,
  onRemove,
  gt,
}: {
  disabled?: boolean;
  onRemove: () => void;
  gt: ReturnType<typeof useGT>;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="size-6"
      disabled={disabled}
      aria-label={gt('Remove parameter binding', {
        context: 'icon button removing a procedure parameter binding',
      })}
      onClick={onRemove}
    >
      <TrashIcon className="size-3.5" />
    </Button>
  );
}

function ParameterDescription({ text }: { text: string }) {
  return <p className="text-[11px] text-muted-foreground">{text}</p>;
}

function UndeclaredNote() {
  return (
    <p className="text-[11px] text-muted-foreground">
      <T context="procedure parameter binding for a name the SOP does not declare">
        Not declared on this procedure.
      </T>
    </p>
  );
}

function UnmappedWarning() {
  return (
    <p className="text-[11px] text-destructive">
      <T context="required procedure parameter has no task value">
        Not mapped. The procedure will not start until this has a value.
      </T>
    </p>
  );
}

function ParameterValue({
  value,
  fields,
  unknownField,
  disabled,
  onChange,
  gt,
}: {
  value: string | undefined;
  fields: readonly ProcedureInputField[];
  unknownField: string | null;
  disabled?: boolean;
  onChange: (value: string | null) => void;
  gt: ReturnType<typeof useGT>;
}) {
  const textMode = parameterTextMode(value);
  return (
    <div className="flex flex-col gap-1">
      <BindingSelect
        value={value}
        fields={fields}
        unknownField={unknownField}
        showInputs={showInputFieldGroup(fields.length, unknownField)}
        disabled={disabled}
        onChange={onChange}
      />
      {textMode == null ? null : (
        <BindingText
          mode={textMode}
          value={value ?? ''}
          disabled={disabled}
          onChange={onChange}
          gt={gt}
        />
      )}
    </div>
  );
}

function BindingSelect({
  value,
  fields,
  unknownField,
  showInputs,
  disabled,
  onChange,
}: {
  value: string | undefined;
  fields: readonly ProcedureInputField[];
  unknownField: string | null;
  showInputs: boolean;
  disabled?: boolean;
  onChange: (value: string | null) => void;
}) {
  return (
    <Select
      value={bindingSelectValue(value)}
      disabled={disabled}
      onValueChange={(next) => onChange(valueAfterBindingChoice(value, next))}
    >
      <SelectTrigger className="h-8">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={BINDING_UNMAPPED}>
          <T context="option, procedure parameter is not passed from the task">
            Not mapped
          </T>
        </SelectItem>
        {showInputs ? (
          <SelectGroup>
            <SelectLabel>
              <T context="group label, task input fields that can feed a procedure parameter">
                Input fields
              </T>
            </SelectLabel>
            {fields.map((field) => (
              <SelectItem
                key={field.fieldName}
                value={inputFieldBinding(field.fieldName)}
              >
                {inputFieldOptionLabel(field)}
              </SelectItem>
            ))}
            {unknownField == null ? null : (
              <SelectItem value={inputFieldBinding(unknownField)}>
                <T context="option, procedure binding points at an input field that is not on the schema">
                  <Var>{unknownField}</Var> (not on this schema)
                </T>
              </SelectItem>
            )}
          </SelectGroup>
        ) : null}
        <SelectGroup>
          <SelectLabel>
            <T context="group label, task anchor values that can feed a procedure parameter">
              Task
            </T>
          </SelectLabel>
          <SelectItem value={TASK_PROCEDURE_PARAM_ANCHOR_ID}>
            <T context="option, bind a procedure parameter to the task anchor id">
              Anchor record id
            </T>
          </SelectItem>
          <SelectItem value={TASK_PROCEDURE_PARAM_ANCHOR_TYPE}>
            <T context="option, bind a procedure parameter to the task anchor type">
              Anchor record type
            </T>
          </SelectItem>
          <SelectItem value={BINDING_LITERAL}>
            <T context="option, bind a procedure parameter to a fixed value">
              Fixed value
            </T>
          </SelectItem>
          <SelectItem value={BINDING_CUSTOM}>
            <T context="option, bind a procedure parameter to a custom task path">
              Custom path
            </T>
          </SelectItem>
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

function BindingText({
  mode,
  value,
  disabled,
  onChange,
  gt,
}: {
  mode: 'literal' | 'custom';
  value: string;
  disabled?: boolean;
  onChange: (value: string | null) => void;
  gt: ReturnType<typeof useGT>;
}) {
  return (
    <Input
      className="h-7 font-mono text-xs"
      value={value}
      disabled={disabled}
      placeholder={bindingTextPlaceholder(mode, gt)}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

function bindingTextPlaceholder(
  mode: 'literal' | 'custom',
  gt: ReturnType<typeof useGT>,
): string {
  if (mode === 'literal') {
    return gt('Fixed value', {
      context: 'placeholder, literal procedure parameter value',
    });
  }
  return gt('$.task.metadata.invoiceNumber', {
    context: 'placeholder, custom task path for a procedure parameter',
  });
}
